import React from "react";
import { useLocation } from "react-router-dom";
import { Box, Chip, FormControlLabel, MenuItem, Paper, Radio, RadioGroup, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { CodeBlock, Expect, Hint, Step, useSteps } from "../guide/GuideParts";
import { DocTabs, Flow, FlowActor, FlowStep, LinkCards, PageHeader, Screenshot, Section } from "../guide/DocParts";
import { AUTHORITY, B2C_USER_TOKEN_URL, Gateway, GatewayId, gateways, MSAL_LINKS, PROBLEMS, TOKEN_URL } from "./gateways";

// Gateways & Auth: CentralGateway, ExternalGateway and the Azure B2C authentication in one place

const ACTORS: FlowActor[] = [
  { id: "client", name: "Client app", sub: "Handelsapp, KundenPortal …", color: "#0277bd" },
  { id: "ms", name: "Microsoft identity", sub: "Azure AD B2C", color: "#5c2d91" },
  { id: "gw", name: "Gateway", sub: "Central / External", color: "#005f98" },
  { id: "api", name: "Micro service", sub: "NewsApi, Internal API …", color: "#00897b" },
  { id: "data", name: "Data", sub: "Storage, ERP", color: "#7b4fc9" },
];

const APP_FLOW: FlowStep[] = [
  { from: "client", to: "ms", text: "The backend sends its client ID, its secret and the scope of the gateway (client credentials)." },
  { from: "ms", to: "client", text: "Microsoft checks the secret and the granted permissions and returns an access token (valid about 1 hour)." },
  { from: "client", to: "gw", text: "The app calls the gateway with the header Authorization: Bearer <token>." },
  { from: "gw", to: "ms", text: "The gateway validates the token (signature, audience, roles ReadAll / WriteAll) and gets its own token for the micro service." },
  { from: "gw", to: "api", text: "The gateway forwards the call with its own token. Clients never reach the micro services directly." },
  { from: "api", to: "data", text: "The micro service reads Azure Storage or the ERP (through the Internal API) …" },
  { from: "api", to: "client", text: "… and the answer goes back through the gateway to the app." },
];

const USER_FLOW: FlowStep[] = [
  { from: "client", to: "ms", text: "The user signs in with the AD account (Material UI, Handelsapp). The user must exist in the directory." },
  { from: "ms", to: "client", text: "Microsoft returns a token for this user and the gateway scope (OAuth authorization code flow)." },
  { from: "client", to: "gw", text: "The app calls the gateway with Authorization: Bearer <token>." },
  { from: "gw", to: "ms", text: "The gateway validates the user token and gets its own token for the micro service." },
  { from: "gw", to: "api", text: "Example: GET newsfeed → NewsApi." },
  { from: "api", to: "data", text: "NewsApi reads the news of the managers from Azure Storage …" },
  { from: "api", to: "client", text: "… and the handlers see the current news in the app." },
];

type Lang = "node" | "powershell" | "curl" | "postman";

const query = (params: Record<string, string>) => new URLSearchParams(params).toString().replace(/%3C/g, "<").replace(/%3E/g, ">");

const codeFor = (gw: Gateway, endpointIndex: number, lang: Lang) => {
  const ep = gw.endpoints[endpointIndex];
  const url = `${gw.baseUrl}${ep.path}?${query(ep.params)}`;
  switch (lang) {
    case "node":
      return {
        env: `AUTHORITY="${AUTHORITY}"\nCLIENT_ID="<client id of your app>"\nCLIENT_SECRET="<secret – never commit it>"\nSCOPE="${gw.scope}"`,
        code: `// npm install @azure/msal-node
const msal = require("@azure/msal-node");

const cca = new msal.ConfidentialClientApplication({
  auth: {
    clientId: process.env.CLIENT_ID,
    authority: process.env.AUTHORITY,
    clientSecret: process.env.CLIENT_SECRET,
  },
});

async function call() {
  // MSAL caches the token and renews it when it expires
  const { accessToken } = await cca.acquireTokenByClientCredential({ scopes: [process.env.SCOPE] });
  const response = await fetch("${url}", {
    headers: { Authorization: \`Bearer \${accessToken}\` },
  });
  if (!response.ok) throw new Error(\`${gw.name} answered \${response.status}\`);
  return response.json();
}

call().then(console.log).catch(console.error);`,
      };
    case "powershell":
      return {
        env: `$env:CLIENT_ID = "<client id of your app>"\n$env:CLIENT_SECRET = "<secret>"`,
        code: `$body = @{
  client_id     = $env:CLIENT_ID
  client_secret = $env:CLIENT_SECRET
  scope         = "${gw.scope}"
  grant_type    = "client_credentials"
}
$token = (Invoke-RestMethod -Method Post -Uri "${TOKEN_URL}" -Body $body).access_token

Invoke-RestMethod -Uri "${url}" -Headers @{ Authorization = "Bearer $token" }`,
      };
    case "curl":
      return {
        env: `export CLIENT_ID="<client id of your app>"\nexport CLIENT_SECRET="<secret>"`,
        code: `TOKEN=$(curl -s -X POST "${TOKEN_URL}" \\
  -d "client_id=$CLIENT_ID" \\
  -d "client_secret=$CLIENT_SECRET" \\
  -d "scope=${gw.scope}" \\
  -d "grant_type=client_credentials" | jq -r .access_token)

curl -H "Authorization: Bearer $TOKEN" "${url}"`,
      };
    case "postman":
      return {
        env: "",
        code: `Request:        GET ${url}
Authorization:  Type = OAuth 2.0 → Configure New Token
  Grant type:       Client Credentials
  Access Token URL: ${TOKEN_URL}
  Client ID:        <client id of your app>
  Client Secret:    <secret>
  Scope:            ${gw.scope}
  Client Authentication: Send as Basic Auth header
→ Get New Access Token → Use Token → Send`,
      };
  }
};

const CodeGenerator: React.FunctionComponent<{ gateway: GatewayId }> = ({ gateway }) => {
  const [gw, setGw] = React.useState<GatewayId>(gateway);
  const [ep, setEp] = React.useState(0);
  const [lang, setLang] = React.useState<Lang>("node");
  React.useEffect(() => setGw(gateway), [gateway]);
  React.useEffect(() => setEp(0), [gw]);
  const { env, code } = codeFor(gateways[gw], ep, lang);
  return (
    <>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
        <ToggleButtonGroup size="small" exclusive value={gw} onChange={(_, v) => v && setGw(v)}>
          <ToggleButton value="central">CentralGateway</ToggleButton>
          <ToggleButton value="external">ExternalGateway</ToggleButton>
        </ToggleButtonGroup>
        <TextField select size="small" label="Endpoint" value={ep} onChange={(e) => setEp(Number(e.target.value))} sx={{ minWidth: 220 }}>
          {gateways[gw].endpoints.map((e, i) => (
            <MenuItem key={e.path} value={i}>
              {e.name}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup size="small" exclusive value={lang} onChange={(_, v) => v && setLang(v)}>
          <ToggleButton value="node">Node.js (MSAL)</ToggleButton>
          <ToggleButton value="powershell">PowerShell</ToggleButton>
          <ToggleButton value="curl">curl</ToggleButton>
          <ToggleButton value="postman">Postman</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {env && <CodeBlock label={lang === "node" ? ".env" : "Variables"} code={env} />}
      <CodeBlock label={lang === "postman" ? "Postman settings" : "Code"} code={code} />
      <Hint>
        Client ID and secret are handed out per client app by Digitalisierung after the app is registered in Azure AD B2C. Keep the secret in the app's settings or a vault – never in code,
        documentation or chat.
      </Hint>
    </>
  );
};

// Decodes a JWT in the browser only, to explain 401/403 errors
const TokenInspector: React.FunctionComponent = () => {
  const [token, setToken] = React.useState("");
  const claims = React.useMemo(() => {
    const part = token.trim().replace(/^Bearer\s+/i, "").split(".")[1];
    if (!part) return undefined;
    try {
      const json = decodeURIComponent(
        atob(part.replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(""),
      );
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [token]);
  const audience = String(claims?.aud ?? "");
  const gw = Object.values(gateways).find((g) => g.scope.startsWith(audience) || (g.appId && audience.includes(g.appId)));
  const exp = typeof claims?.exp === "number" ? new Date(claims.exp * 1000) : undefined;
  const roles = Array.isArray(claims?.roles) ? (claims!.roles as string[]) : [];

  return (
    <>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label="Paste an access token (decoded only in your browser, nothing is sent)"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        sx={{ "& textarea": { fontFamily: "Consolas, monospace", fontSize: 12 } }}
      />
      {claims === null && <Hint type="danger">This is not a valid token (expected three parts separated by dots).</Hint>}
      {claims && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 1.5, mt: 1.5 }}>
          <Hint type={gw ? "ok" : "warn"}>
            <b>Audience (aud)</b>
            <br />
            {audience || "–"}
            <br />
            {gw ? `= ${gw.name}` : "Not one of our gateways"}
          </Hint>
          <Hint type={exp && exp > new Date() ? "ok" : "danger"}>
            <b>Expires (exp)</b>
            <br />
            {exp ? exp.toLocaleString() : "–"}
            <br />
            {exp && exp > new Date() ? "still valid" : "expired → 401"}
          </Hint>
          <Hint type={roles.length ? "ok" : "warn"}>
            <b>Roles</b>
            <br />
            {roles.length ? roles.join(", ") : "none → 403 for app tokens"}
          </Hint>
          <Hint>
            <b>App / user</b>
            <br />
            {String(claims.azp ?? claims.appid ?? "–")}
            <br />
            {String(claims.name ?? claims.preferred_username ?? (claims.oid ? "app token" : ""))}
          </Hint>
        </Box>
      )}
    </>
  );
};

const GatewayPanel: React.FunctionComponent<{ gw: Gateway }> = ({ gw }) => (
  <>
    <Typography variant="body1" sx={{ mb: 1.5 }}>
      {gw.purpose}
    </Typography>
    <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.75, fontSize: 13.5, mb: 2, "& code": { fontFamily: "Consolas, monospace", fontSize: 12.5 } }}>
      <Box color="text.secondary">App Service</Box>
      <code>{gw.appService}</code>
      <Box color="text.secondary">Base URL</Box>
      <code>{gw.baseUrl}</code>
      <Box color="text.secondary">Scope</Box>
      <code>{gw.scope}</code>
      {gw.appId && (
        <>
          <Box color="text.secondary">Application ID</Box>
          <code>{gw.appId}</code>
        </>
      )}
      <Box color="text.secondary">App roles</Box>
      <span>{gw.roles.map(([r, d]) => `${r} (${d})`).join(" · ")}</span>
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 2 }}>
      <div>
        <Typography variant="subtitle2">Used by</Typography>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.5 }}>
          {gw.consumers.map((c) => (
            <Chip key={c} size="small" label={c} color="info" variant="outlined" />
          ))}
        </Box>
      </div>
      <div>
        <Typography variant="subtitle2">Forwards to</Typography>
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 0.5 }}>
          {gw.backends.map((c) => (
            <Chip key={c} size="small" label={c} color="success" variant="outlined" />
          ))}
        </Box>
      </div>
    </Box>
    <Typography variant="subtitle2" sx={{ mt: 2 }}>
      Example calls
    </Typography>
    {gw.endpoints.map((ep) => (
      <Box key={ep.path} sx={{ fontSize: 13, mt: 0.5 }}>
        <b>{ep.name}</b>: <code style={{ fontFamily: "Consolas, monospace" }}>GET {ep.path}?{query(ep.params)}</code> → {ep.forwardsTo}
      </Box>
    ))}
    <Box sx={{ mt: 2 }}>
      <LinkCards links={[{ title: `${gw.name} Swagger`, text: "All endpoints; Authorize with your Bearer token to try them", url: gw.swagger }]} />
    </Box>
  </>
);

const AuthPanel: React.FunctionComponent = () => (
  <>
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderTop: "4px solid #0277bd" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          1 · Apps that sign in a user
        </Typography>
        <Typography variant="body2">
          Material UI, Handelsapp, Sandbox … The user signs in with the AD account and must exist in the directory. Their app roles (e.g. ADMIN) come from the users table of the
          Internal API (PartitionKey = e-mail, RowKey = APP_ROLE).
        </Typography>
        <CodeBlock label="User token endpoint (B2C policy)" code={B2C_USER_TOKEN_URL} />
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderTop: "4px solid #5c2d91" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          2 · Apps that authenticate themselves
        </Typography>
        <Typography variant="body2">
          Backends like KundenPortal or Abfuhrkalender. They hold a <b>client secret</b> generated in Azure AD B2C and use the client credentials flow. Secrets expire – plan the renewal.
        </Typography>
        <CodeBlock label="App token endpoint" code={TOKEN_URL} />
      </Paper>
    </Box>
    <Hint>
      An app gets a valid token for our gateways only when it is (1) <b>registered</b> in Azure AD B2C, (2) has a <b>secret</b> and (3) has the <b>permission</b> (app role) for the
      gateway, granted by an admin.
    </Hint>
  </>
);

const RegisterGuide: React.FunctionComponent = () => {
  const [task, setTask] = React.useState<"new" | "renew" | "permission">("new");
  const { isDone, toggle, count } = useSteps(task);
  const total = task === "new" ? 6 : task === "renew" ? 5 : 3;
  return (
    <>
      <RadioGroup row value={task} onChange={(e) => setTask(e.target.value as typeof task)}>
        <FormControlLabel value="new" control={<Radio size="small" />} label="Register a new client app" />
        <FormControlLabel value="renew" control={<Radio size="small" />} label="Renew an expiring secret" />
        <FormControlLabel value="permission" control={<Radio size="small" />} label="Give an app access to a gateway" />
      </RadioGroup>
      <Typography variant="caption" color="text.secondary">
        {count} of {total} steps done
      </Typography>
      {task === "new" && (
        <>
          <Step n={1} title="Open Azure AD B2C" where="b2c" done={isDone(1)} onToggle={toggle(1)}>
            <p>
              Sign in to the Azure Portal, switch to the directory <b>GENESISCLOUDAPPS</b> (top right) and open <b>Azure AD B2C</b>.
            </p>
            <Screenshot src="/b2c.png" caption="Azure services → Azure AD B2C (directory GENESISCLOUDAPPS)" />
          </Step>
          <Step n={2} title="Create the app registration" where="b2c" done={isDone(2)} onToggle={toggle(2)}>
            <p>
              <b>App registrations → New registration</b>. Name it after the client (e.g. KundenPortalBackend), account type “Accounts in any identity provider…”, no redirect URI for a
              backend. Note the <b>Application (client) ID</b>.
            </p>
          </Step>
          <Step n={3} title="Create a client secret" where="b2c" done={isDone(3)} onToggle={toggle(3)}>
            <p>
              <b>Certificates &amp; secrets → Client secrets → New client secret</b>. Choose the expiry (max. 24 months) and copy the <b>Value</b> immediately – it is shown only once. Put
              the expiry date in the team calendar.
            </p>
            <Screenshot src="/AzureSecrets.PNG" caption="Certificates & secrets: description, expiry, value (shown once)" />
          </Step>
          <Step n={4} title="Add the gateway permission" where="b2c" done={isDone(4)} onToggle={toggle(4)}>
            <p>
              <b>API permissions → Add a permission → My APIs</b> → CentralGateway / ExternalGateway / ERPDataGateway → <b>Application permissions</b> → ReadAll and/or WriteAll.
            </p>
          </Step>
          <Step n={5} title="Grant admin consent" where="b2c" done={isDone(5)} onToggle={toggle(5)}>
            <p>
              Click <b>Grant admin consent for genesiscloudapps</b>. Only admins can do this; without it the token has no roles and the gateway answers 403.
            </p>
            <Screenshot src="/AzureB2CAppPermissions.jpg" caption="API permissions with admin consent; right: the CentralGateway registration" />
          </Step>
          <Step n={6} title="Hand over and test" where="pc" done={isDone(6)} onToggle={toggle(6)}>
            <p>Give client ID and secret to the client team over a secure channel (vault), never by e-mail. Test with the code generator and check the token with the token inspector.</p>
          </Step>
        </>
      )}
      {task === "renew" && (
        <>
          <Hint type="warn">The old secret keeps working until it expires. Create the new one first, deploy it, then delete the old one – no downtime.</Hint>
          <Step n={1} title="Find the app" where="b2c" done={isDone(1)} onToggle={toggle(1)}>
            <p>Azure AD B2C → App registrations → All applications → the client app (e.g. KundenPortalBackend).</p>
          </Step>
          <Step n={2} title="Create the new secret" where="b2c" done={isDone(2)} onToggle={toggle(2)}>
            <p>Certificates &amp; secrets → New client secret → copy the value.</p>
          </Step>
          <Step n={3} title="Update the client app" where="browser" done={isDone(3)} onToggle={toggle(3)}>
            <p>
              Put the new value into the app's settings (App Service → Configuration, .env on the server, or the vault) and <b>redeploy / restart</b> the app – contact Digitalisierung
              if you are not sure where it is configured.
            </p>
          </Step>
          <Step n={4} title="Check the app works" where="browser" done={isDone(4)} onToggle={toggle(4)}>
            <p>Open the app or watch its log stream: calls must answer 200, no AADSTS7000215.</p>
          </Step>
          <Step n={5} title="Delete the old secret" where="b2c" done={isDone(5)} onToggle={toggle(5)}>
            <p>Certificates &amp; secrets → old secret → Delete.</p>
          </Step>
        </>
      )}
      {task === "permission" && (
        <>
          <Step n={1} title="Open the client app's API permissions" where="b2c" done={isDone(1)} onToggle={toggle(1)}>
            <Screenshot src="/Permissions.PNG" caption="Configured permissions: CentralGateway, ERPDataGateway (ReadAll / WriteAll), Microsoft Graph" />
          </Step>
          <Step n={2} title="Add the role" where="b2c" done={isDone(2)} onToggle={toggle(2)}>
            <p>Add a permission → My APIs → the gateway → Application permissions → ReadAll (read) or WriteAll (write).</p>
          </Step>
          <Step n={3} title="Grant admin consent and get a new token" where="b2c" done={isDone(3)} onToggle={toggle(3)}>
            <p>
              Grant admin consent. Tokens issued before still have the old roles – the app gets the new role with its next token (at the latest after about one hour, or after a restart).
            </p>
            <Expect>
              the token inspector shows the new role in <b>roles</b>.
            </Expect>
          </Step>
        </>
      )}
    </>
  );
};

const TAB_BY_ROUTE: Record<string, string> = { central: "central", external: "external", auth: "auth" };

export const GatewaysAuthPage: React.FunctionComponent = () => {
  const location = useLocation();
  const initial = TAB_BY_ROUTE[location.pathname.split("/").pop() ?? ""] ?? "central";
  const [mode, setMode] = React.useState<"app" | "user">("app");
  const [gateway, setGateway] = React.useState<GatewayId>(initial === "external" ? "external" : "central");

  return (
    <Paper sx={{ p: 2, width: "100%" }}>
      <PageHeader
        title="Gateways & Auth"
        subtitle="CentralGateway and ExternalGateway on Azure App Service · tokens from Azure AD B2C (directory genesiscloudapps)"
        stats={[
          { label: "Gateways", value: 2 },
          { label: "Client apps", value: gateways.central.consumers.length + gateways.external.consumers.length },
          { label: "Token lifetime", value: "~1 h" },
        ]}
      />

      <Section title="How a call works" text="Press Play or click the numbers">
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)} sx={{ mb: 1 }}>
          <ToggleButton value="app">Backend app (client credentials)</ToggleButton>
          <ToggleButton value="user">User sign-in (Handelsapp news)</ToggleButton>
        </ToggleButtonGroup>
        <Flow actors={ACTORS} steps={mode === "app" ? APP_FLOW : USER_FLOW} />
      </Section>

      <Section title="The gateways">
        <DocTabs
          initial={initial}
          tabs={[
            { id: "central", label: "CentralGateway", content: <GatewayPanel gw={gateways.central} /> },
            { id: "external", label: "ExternalGateway", content: <GatewayPanel gw={gateways.external} /> },
            { id: "auth", label: "Authentication (B2C)", content: <AuthPanel /> },
          ]}
        />
      </Section>

      <Section title="Try it" text="Ready-to-run code to get a token and call a gateway">
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          {(["central", "external"] as GatewayId[]).map((g) => (
            <Chip key={g} label={gateways[g].name} color={gateway === g ? "primary" : "default"} onClick={() => setGateway(g)} />
          ))}
        </Box>
        <CodeGenerator gateway={gateway} />
      </Section>

      <Section title="Token inspector" text="Why does the gateway answer 401 or 403?">
        <TokenInspector />
      </Section>

      <Section title="Admin tasks in Azure AD B2C">
        <RegisterGuide />
      </Section>

      <Section title="Troubleshooting">
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13, "& th, & td": { textAlign: "left", p: 0.75, borderBottom: 1, borderColor: "divider", verticalAlign: "top" }, "& th": { color: "text.secondary", fontWeight: 600 } }}>
          <thead>
            <tr>
              <th>Symptom</th>
              <th>Cause</th>
              <th>Check / fix</th>
            </tr>
          </thead>
          <tbody>
            {PROBLEMS.map(([s, c, f]) => (
              <tr key={s}>
                <td>
                  <b>{s}</b>
                </td>
                <td>{c}</td>
                <td>{f}</td>
              </tr>
            ))}
          </tbody>
        </Box>
        <Typography variant="subtitle2" sx={{ mt: 2 }}>
          Watch the gateway live
        </Typography>
        <Typography variant="body2">
          Azure Portal → App Service <code>{gateways.central.appService}</code> → Monitoring → <b>Log stream</b>. Every call shows the forwarded request (e.g. NewsFeed → newsapi), the
          token request and the status code.
        </Typography>
        <Screenshot src="/AzureServiceLogs.jpg" caption="Log stream of the CentralGateway: NewsFeed → newsapi, token request, RecyclingFootprint → masterdataapi" />
      </Section>

      <Section title="Further reading">
        <LinkCards links={MSAL_LINKS} />
      </Section>
    </Paper>
  );
};
